import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Reusable Stage dropdown that always offers an inline "+ Add new stage…" option
 * (Admin / Manager only). On pick, creates the stage on the project and selects
 * it immediately. Workers see a plain select with no add option.
 *
 * Used on Materials, Labour, Additional items, Subcontractors, plan-analysis
 * results, merchant importer stage review, and project subcontractor quotes.
 */
export default function StageSelect({
  projectId, value, stages, onChange, onStagesChanged,
  className = "input", "data-testid": testid,
  placeholder = "— Unassigned —", allowUnassigned = true,
  disabled = false,
}) {
  const { user } = useAuth();
  const canAdd = user?.role === "Admin" || user?.role === "Manager";
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function create() {
    const name = draft.trim();
    if (!name) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/stages", { project_id: projectId, name });
      setAdding(false); setDraft("");
      await onStagesChanged?.();
      onChange?.(data.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create stage");
    } finally { setBusy(false); }
  }

  if (adding) {
    return (
      <div className="flex items-center gap-1" data-testid={testid ? `${testid}-adding` : undefined}>
        <input autoFocus className={className + " text-xs"} value={draft}
               onChange={e => setDraft(e.target.value)}
               onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); create(); } if (e.key === "Escape") setAdding(false); }}
               placeholder="New stage name"
               data-testid={testid ? `${testid}-newname` : "stage-newname"}/>
        <button type="button" className="btn-silver text-xs" disabled={busy} onClick={create}
                data-testid={testid ? `${testid}-newsave` : "stage-newsave"}>
          {busy ? "…" : "Add"}
        </button>
        <button type="button" className="text-xs opacity-70" onClick={() => { setAdding(false); setDraft(""); setErr(""); }}>×</button>
        {err && <span className="text-red-400 text-[10px]">{err}</span>}
      </div>
    );
  }

  return (
    <select
      className={className}
      value={value || ""}
      disabled={disabled}
      onChange={(e) => {
        if (e.target.value === "__add__") { setAdding(true); return; }
        onChange?.(e.target.value || null);
      }}
      data-testid={testid}
    >
      {allowUnassigned && <option value="">{placeholder}</option>}
      {(stages || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      {canAdd && <option value="__add__" data-testid={testid ? `${testid}-addopt` : "stage-addopt"}>+ Add new stage…</option>}
    </select>
  );
}
